import { useState, useEffect } from "react";
import * as ort from "onnxruntime-web";

const MODEL_URL = "/models/document-detect/yolov8n-doc.onnx";

let _session = null;
let _promise = null;
let _ready = false;
let _error = null;
let _listeners = [];

function notify() {
  _listeners.forEach((fn) => fn());
}

/**
 * Kick off model download + ONNX session creation.
 * Returns a Promise that resolves when the session is ready (or rejects on failure).
 * Safe to call multiple times — only the first call triggers the actual load.
 */
export function preloadYoloModel() {
  if (_promise) return _promise;

  _promise = (async () => {
    try {
      console.log("[YoloCache] Preloading model...");

      ort.env.logLevel = "error";
      ort.env.wasm.wasmPaths = "/";
      ort.env.wasm.numThreads = 1;
      ort.env.wasm.proxy = false;

      const session = await ort.InferenceSession.create(MODEL_URL, {
        executionProviders: ["wasm"],
        graphOptimizationLevel: "all",
      });

      _session = session;
      _ready = true;
      console.log("[YoloCache] Model preloaded successfully");
      notify();
      return session;
    } catch (err) {
      console.warn("[YoloCache] Model preload failed:", err.message);
      _error = err;
      _ready = true; // mark ready so the button enables (fallback to JS contours)
      notify();
      throw err;
    }
  })();

  return _promise;
}

/** Returns the cached InferenceSession, or null if not yet loaded / failed. */
export function getPreloadedSession() {
  return _session;
}

/** Synchronous check: has preloading finished (success or failure)? */
export function isModelReady() {
  return _ready;
}

/**
 * React hook that triggers preload and returns { modelReady, modelError }.
 * Re-renders the component when the preload finishes.
 */
export function useYoloPreload(skip = false) {
  const [ready, setReady] = useState(_ready);
  const [error, setError] = useState(_error);

  useEffect(() => {
    if (skip) return;

    // If already done, sync state immediately
    if (_ready) {
      setReady(true);
      setError(_error);
      return;
    }

    const listener = () => {
      setReady(_ready);
      setError(_error);
    };
    _listeners.push(listener);

    // Kick off preload (no-op if already started)
    preloadYoloModel().catch(() => {});

    return () => {
      _listeners = _listeners.filter((fn) => fn !== listener);
    };
  }, [skip]);

  return { modelReady: ready, modelError: error };
}
